import { useState } from "react";
import { Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { TEAMS, JERSEY_PRICE } from "@/cleanteamData";

export const JerseySVG = ({ primary, secondary, className = "" }) => (
  <svg viewBox="0 0 100 110" className={className} aria-hidden="true">
    <path d="M32 12 L43 6 Q50 15 57 6 L68 12 L86 28 L76 41 L71 35 L71 102 L29 102 L29 35 L24 41 L14 28 Z"
      fill={primary} stroke={secondary} strokeWidth="3" strokeLinejoin="round" />
  </svg>
);

export const JerseyWithLogo = ({ team, className = "", logoClass = "w-2/5" }) => (
  <span className={`relative inline-flex items-center justify-center ${className}`}>
    <JerseySVG primary={team.primary} secondary={team.secondary} className="w-full h-full" />
    <img src={`https://a.espncdn.com/i/teamlogos/nfl/500/${team.id}.png`} alt={`${team.name} logo`}
      loading="lazy" className={`absolute top-[38%] left-1/2 -translate-x-1/2 -translate-y-1/4 ${logoClass} drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]`} />
  </span>
);

export default function TeamGrid({ onSelect }) {
  const [conf, setConf] = useState("ALL");
  const teams = conf === "ALL" ? TEAMS : TEAMS.filter((t) => t.conf === conf);

  return (
    <section id="teams" className="py-16 md:py-24 bg-[#07090E]" data-testid="teams-section">
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-widest text-[#39FF14]" data-testid="teams-eyebrow">The Full Roster</p>
          <h2 className="font-['Barlow_Condensed',sans-serif] text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white mt-2" data-testid="teams-title">
            Pick Your Colors
          </h2>
          <p className="mt-3 text-base text-white/60 max-w-2xl" data-testid="teams-subtitle">
            All 32 teams, stitched and ready. Every jersey ${JERSEY_PRICE} — choose your team, then your size.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex gap-2" data-testid="conf-filters">
            {["ALL", "AFC", "NFC"].map((c) => (
              <button key={c} onClick={() => setConf(c)} data-testid={`conf-filter-${c.toLowerCase()}`}
                className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                  conf === c ? "bg-[#39FF14] text-[#07090E]" : "border border-white/15 text-white/60 hover:border-[#39FF14]/50 hover:text-white"
                }`}>
                {c === "ALL" ? "All 32 Teams" : c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {teams.map((t, i) => (
            <Reveal key={t.id} delay={Math.min(i % 4, 3) * 0.06} y={26}>
              <button onClick={() => onSelect(t)} data-testid={`team-card-${t.id}`}
                className="group w-full text-left rounded-2xl overflow-hidden border border-white/10 bg-[#0F141F] hover:bg-[#161D2D] hover:border-[#39FF14]/50 hover:-translate-y-1 transition-all duration-300">
                <span className="relative block aspect-square p-6 items-center justify-center flex"
                  style={{ background: `radial-gradient(circle at 50% 35%, ${t.primary}33, transparent 70%)` }}>
                  <JerseyWithLogo team={t}
                    className="w-3/5 h-3/5 drop-shadow-[0_12px_24px_rgba(0,0,0,0.5)] group-hover:scale-110 group-hover:-rotate-2 transition-transform duration-300" />
                  <span className="absolute top-3 right-3 font-['Barlow_Condensed',sans-serif] text-lg font-extrabold tracking-widest"
                    style={{ color: t.secondary }}>{t.abbr}</span>
                </span>
                <span className="block p-4 border-t border-white/5">
                  <span className="block text-sm font-bold uppercase tracking-wide text-white" data-testid={`team-name-${t.id}`}>{t.name}</span>
                  <span className="mt-1.5 flex items-center gap-1.5" data-testid={`team-stars-${t.id}`}>
                    <span className="flex text-[#FFD700]">
                      {[...Array(5)].map((_, s) => <Star key={s} size={11} fill="currentColor" strokeWidth={0} />)}
                    </span>
                    <span className="text-[11px] text-white/40">({t.reviews})</span>
                  </span>
                  <span className="mt-2 flex items-center justify-between">
                    <span className="font-['Barlow_Condensed',sans-serif] text-xl font-extrabold text-[#39FF14]">${JERSEY_PRICE}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 group-hover:text-[#39FF14] transition-colors">Select Size →</span>
                  </span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
